import { useCallback, useEffect, useState } from "react";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, X, Maximize2 } from "lucide-react";

// Premium interactive gallery (embla): swipe on mobile, arrows on desktop,
// photo counter, fullscreen lightbox, lazy-loaded images.
export const Gallery = ({ images = [], alt = "Photo", aspect = "aspect-[16/10]" }) => {
  const [emblaRef, embla] = useEmblaCarousel({ loop: images.length > 1, align: "start" });
  const [selected, setSelected] = useState(0);
  const [lightbox, setLightbox] = useState(-1);

  const onSelect = useCallback(() => embla && setSelected(embla.selectedScrollSnap()), [embla]);
  useEffect(() => {
    if (!embla) return;
    onSelect();
    embla.on("select", onSelect);
    return () => embla.off("select", onSelect);
  }, [embla, onSelect]);

  if (!images.length) return null;

  const scrollPrev = (e) => { e?.stopPropagation(); embla && embla.scrollPrev(); };
  const scrollNext = (e) => { e?.stopPropagation(); embla && embla.scrollNext(); };
  const single = images.length === 1;

  return (
    <>
      <div className="relative group/gal">
        <div className="overflow-hidden" ref={emblaRef} data-testid="gallery">
          <div className="flex">
            {images.map((src, i) => (
              <div key={i} className="relative flex-[0_0_100%] min-w-0">
                <button
                  type="button"
                  onClick={() => setLightbox(i)}
                  className={`block w-full ${aspect} overflow-hidden bg-[#E4DCCF] cursor-zoom-in`}
                  data-testid={`gallery-slide-${i}`}
                  aria-label="Agrandir la photo"
                >
                  <img src={src} alt={`${alt} ${i + 1}`} loading="lazy"
                       className="h-full w-full object-cover transition-transform duration-700 group-hover/gal:scale-[1.02]" />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Fullscreen hint */}
        <span className="absolute top-3 right-3 z-10 flex items-center gap-1 bg-black/45 text-white text-[10px] uppercase tracking-[0.15em] px-2.5 py-1.5 backdrop-blur-sm pointer-events-none">
          <Maximize2 className="h-3 w-3" /> Plein écran
        </span>

        {!single && (
          <>
            <button type="button" onClick={scrollPrev} data-testid="gallery-prev"
              className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#1A1B18] items-center justify-center shadow-md hidden md:flex opacity-0 group-hover/gal:opacity-100 transition-opacity"
              aria-label="Photo précédente"><ChevronLeft className="h-5 w-5" /></button>
            <button type="button" onClick={scrollNext} data-testid="gallery-next"
              className="absolute right-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 rounded-full bg-white/90 hover:bg-white text-[#1A1B18] items-center justify-center shadow-md hidden md:flex opacity-0 group-hover/gal:opacity-100 transition-opacity"
              aria-label="Photo suivante"><ChevronRight className="h-5 w-5" /></button>
            <div className="absolute bottom-3 right-3 z-10 bg-black/55 text-white text-[11px] tracking-wide px-2.5 py-1 backdrop-blur-sm" data-testid="gallery-counter">
              {selected + 1} / {images.length}
            </div>
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-1.5">
              {images.map((_, i) => (
                <span key={i} className={`h-1.5 rounded-full transition-all ${i === selected ? "w-5 bg-white" : "w-1.5 bg-white/50"}`} />
              ))}
            </div>
          </>
        )}
      </div>

      {lightbox >= 0 && (
        <Lightbox images={images} start={lightbox} alt={alt} onClose={() => setLightbox(-1)} />
      )}
    </>
  );
};

const Lightbox = ({ images, start, alt, onClose }) => {
  const [i, setI] = useState(start);
  const prev = useCallback((e) => { e?.stopPropagation(); setI((v) => (v - 1 + images.length) % images.length); }, [images.length]);
  const next = useCallback((e) => { e?.stopPropagation(); setI((v) => (v + 1) % images.length); }, [images.length]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { document.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose, prev, next]);

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center" onClick={onClose} data-testid="lightbox">
      <button type="button" onClick={onClose} className="absolute top-5 right-5 text-white/80 hover:text-white" aria-label="Fermer" data-testid="lightbox-close"><X className="h-7 w-7" /></button>
      {images.length > 1 && (
        <>
          <button type="button" onClick={prev} className="absolute left-4 md:left-8 text-white/80 hover:text-white" aria-label="Précédent" data-testid="lightbox-prev"><ChevronLeft className="h-9 w-9" /></button>
          <button type="button" onClick={next} className="absolute right-4 md:right-8 text-white/80 hover:text-white" aria-label="Suivant" data-testid="lightbox-next"><ChevronRight className="h-9 w-9" /></button>
        </>
      )}
      <img src={images[i]} alt={`${alt} ${i + 1}`} className="max-h-[88vh] max-w-[92vw] object-contain" onClick={(e) => e.stopPropagation()} />
      {images.length > 1 && (
        <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-white/85 text-sm tracking-wide">{i + 1} / {images.length}</div>
      )}
    </div>
  );
};

export default Gallery;
